/** @jsxImportSource @emotion/react */
import React, { useState } from 'react'
import { css, keyframes } from '@emotion/react'
import colors from '@sportninja/common/constants/appColors'
import type {
  DivisionJob,
  SchedulerFlightCapabilities,
} from '../hooks/useSchedulerGeneration'

const fadeInUp = keyframes`
  0% { opacity: 0; transform: translateY(12px); }
  100% { opacity: 1; transform: translateY(0); }
`

interface PartiallyGeneratedStateProps {
  divisionJobs: DivisionJob[]
  capabilities: SchedulerFlightCapabilities | undefined
  satisfactionScore: number | null
  onRetryFailed: () => void
  onResetAll: () => void
  onApproveCompleted: () => void
  retrying: boolean
}

/**
 * Shown when status = 'partially_generated'.
 * Displays which divisions completed and which failed,
 * with action buttons driven by capabilities.
 */
const PartiallyGeneratedState: React.FC<PartiallyGeneratedStateProps> = ({
  divisionJobs,
  capabilities,
  satisfactionScore,
  onRetryFailed,
  onResetAll,
  onApproveCompleted,
  retrying,
}) => {
  const completedJobs = divisionJobs.filter((j) => j.status === 'completed')
  const failedJobs = divisionJobs.filter((j) => j.status === 'failed')
  const totalGames = completedJobs.reduce((sum, j) => sum + j.games_count, 0)

  const [expandFailed, setExpandFailed] = useState(true)

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 40px 0;
        animation: ${fadeInUp} 0.4s ease-out;
      `}
    >
      {/* Header icon */}
      <div
        css={css`
          width: 64px;
          height: 64px;
          border-radius: 50%;
          background: linear-gradient(
            135deg,
            ${colors.WARNING_300}20 0%,
            ${colors.WARNING_300}08 100%
          );
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 16px;
        `}
      >
        <svg width='32' height='32' viewBox='0 0 24 24' fill='none'>
          <path
            d='M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z'
            fill={colors.WARNING_300}
          />
        </svg>
      </div>

      {/* Title */}
      <p
        css={css`
          font-family: 'Barlow Condensed', sans-serif;
          font-weight: 600;
          font-size: 24px;
          color: ${colors.WHITE};
          margin: 0 0 8px;
        `}
      >
        Partially Generated
      </p>

      {/* Summary */}
      <p
        css={css`
          font-family: 'Inter', sans-serif;
          font-weight: 400;
          font-size: 14px;
          color: ${colors.NEUTRAL_50};
          margin: 0 0 24px;
          text-align: center;
        `}
      >
        {completedJobs.length}/{divisionJobs.length} divisions scheduled (
        {totalGames} game{totalGames !== 1 ? 's' : ''}). {failedJobs.length}{' '}
        division{failedJobs.length !== 1 ? 's' : ''} failed.
      </p>

      {/* Score if available */}
      {satisfactionScore !== null && (
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 8px 16px;
            background: ${colors.SECONDARY_500};
            border: 1px solid ${colors.SECONDARY_200};
            border-radius: 8px;
            margin-bottom: 24px;
          `}
        >
          <span
            css={css`
              font-family: 'Inter', sans-serif;
              font-size: 13px;
              color: ${colors.NEUTRAL_50};
            `}
          >
            Quality Score:
          </span>
          <span
            css={css`
              font-family: 'Inter', sans-serif;
              font-weight: 600;
              font-size: 14px;
              color: ${satisfactionScore >= 90
                ? colors.SUCCESS_300
                : satisfactionScore >= 70
                ? colors.WARNING_300
                : colors.ERROR_300};
            `}
          >
            {satisfactionScore.toFixed(1)}%
          </span>
        </div>
      )}

      {/* Completed divisions */}
      {completedJobs.length > 0 && (
        <div
          css={css`
            width: 100%;
            max-width: 640px;
            margin-bottom: 16px;
          `}
        >
          <p
            css={css`
              font-family: 'Inter', sans-serif;
              font-weight: 600;
              font-size: 12px;
              color: ${colors.SUCCESS_300};
              text-transform: uppercase;
              letter-spacing: 0.5px;
              margin: 0 0 8px;
            `}
          >
            Completed ({completedJobs.length})
          </p>
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 6px;
            `}
          >
            {completedJobs.map((job) => (
              <div
                key={job.id}
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 12px;
                  padding: 10px 16px;
                  background: rgba(16, 173, 50, 0.04);
                  border: 1px solid rgba(16, 173, 50, 0.15);
                  border-radius: 8px;
                `}
              >
                <svg width='18' height='18' viewBox='0 0 24 24' fill='none'>
                  <circle cx='12' cy='12' r='10' fill={colors.SUCCESS_300} />
                  <path
                    d='M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z'
                    fill={colors.WHITE}
                  />
                </svg>
                <span
                  css={css`
                    font-family: 'Inter', sans-serif;
                    font-weight: 500;
                    font-size: 14px;
                    color: ${colors.WHITE};
                    flex: 1;
                  `}
                >
                  {job.division_name}
                </span>
                {job.satisfaction_score !== null && (
                  <span
                    css={css`
                      font-family: 'Inter', sans-serif;
                      font-size: 13px;
                      color: ${colors.SUCCESS_300};
                    `}
                  >
                    {job.satisfaction_score.toFixed(1)}%
                  </span>
                )}
                <span
                  css={css`
                    font-family: 'Inter', sans-serif;
                    font-size: 12px;
                    color: ${colors.NEUTRAL_70};
                  `}
                >
                  {job.games_count} game{job.games_count !== 1 ? 's' : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Failed divisions */}
      {failedJobs.length > 0 && (
        <div
          css={css`
            width: 100%;
            max-width: 640px;
            margin-bottom: 24px;
          `}
        >
          <button
            type='button'
            onClick={() => setExpandFailed((prev) => !prev)}
            css={css`
              display: flex;
              align-items: center;
              gap: 4px;
              background: transparent;
              border: none;
              padding: 0;
              cursor: pointer;
              margin-bottom: 8px;
            `}
          >
            <p
              css={css`
                font-family: 'Inter', sans-serif;
                font-weight: 600;
                font-size: 12px;
                color: ${colors.ERROR_300};
                text-transform: uppercase;
                letter-spacing: 0.5px;
                margin: 0;
              `}
            >
              Failed ({failedJobs.length})
            </p>
            <svg
              width='16'
              height='16'
              viewBox='0 0 24 24'
              fill='none'
              css={css`
                transform: ${expandFailed ? 'rotate(180deg)' : 'rotate(0)'};
                transition: transform 0.2s ease;
              `}
            >
              <path
                d='M7.41 8.59L12 13.17L16.59 8.59L18 10L12 16L6 10L7.41 8.59Z'
                fill={colors.ERROR_300}
              />
            </svg>
          </button>
          {expandFailed && (
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 6px;
              `}
            >
              {failedJobs.map((job) => (
                <div
                  key={job.id}
                  css={css`
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                    padding: 10px 16px;
                    background: rgba(217, 27, 27, 0.04);
                    border: 1px solid rgba(217, 27, 27, 0.15);
                    border-radius: 8px;
                  `}
                >
                  <div
                    css={css`
                      display: flex;
                      align-items: center;
                      gap: 12px;
                    `}
                  >
                    <svg width='18' height='18' viewBox='0 0 24 24' fill='none'>
                      <circle cx='12' cy='12' r='10' fill={colors.ERROR_300} />
                      <path
                        d='M15.59 7L12 10.59 8.41 7 7 8.41 10.59 12 7 15.59 8.41 17 12 13.41 15.59 17 17 15.59 13.41 12 17 8.41z'
                        fill={colors.WHITE}
                      />
                    </svg>
                    <span
                      css={css`
                        font-family: 'Inter', sans-serif;
                        font-weight: 500;
                        font-size: 14px;
                        color: ${colors.WHITE};
                        flex: 1;
                      `}
                    >
                      {job.division_name}
                    </span>
                    {job.error_code && (
                      <span
                        css={css`
                          font-family: 'Inter', sans-serif;
                          font-size: 12px;
                          color: ${colors.ERROR_200};
                          background: rgba(217, 27, 27, 0.1);
                          padding: 2px 8px;
                          border-radius: 4px;
                        `}
                      >
                        {job.error_code}
                      </span>
                    )}
                  </div>
                  {job.error_message && (
                    <p
                      css={css`
                        font-family: 'Inter', sans-serif;
                        font-size: 12px;
                        color: ${colors.NEUTRAL_70};
                        margin: 0 0 0 30px;
                      `}
                    >
                      {job.error_message}
                    </p>
                  )}
                  {job.violations?.length > 0 && (
                    <div
                      css={css`
                        margin: 4px 0 0 30px;
                      `}
                    >
                      {job.violations.map((v, idx) => (
                        <div
                          key={idx}
                          css={css`
                            margin-bottom: 4px;
                          `}
                        >
                          {v.resolution_suggestion && (
                            <p
                              css={css`
                                font-family: 'Inter', sans-serif;
                                font-size: 12px;
                                color: ${colors.WARNING_200};
                                margin: 2px 0 0;
                              `}
                            >
                              {v.resolution_suggestion}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Action buttons */}
      <div
        css={css`
          display: flex;
          gap: 12px;
          width: 100%;
          max-width: 640px;
          justify-content: center;
        `}
      >
        {capabilities?.can_retry && (
          <button
            type='button'
            onClick={onRetryFailed}
            disabled={retrying}
            css={css`
              padding: 10px 24px;
              background: ${colors.PRIMARY};
              border: none;
              border-radius: 8px;
              color: ${colors.PRIMARY_TEXT};
              font-family: 'Inter', sans-serif;
              font-size: 14px;
              font-weight: 600;
              cursor: pointer;
              transition: background 0.2s ease;
              &:hover {
                background: ${colors.PRIMARY_DARK};
              }
              &:disabled {
                opacity: 0.6;
                cursor: not-allowed;
              }
            `}
          >
            {retrying ? 'Retrying...' : 'Retry Failed Divisions'}
          </button>
        )}

        {capabilities?.can_reset && (
          <button
            type='button'
            onClick={onResetAll}
            disabled={retrying}
            css={css`
              padding: 10px 24px;
              background: transparent;
              border: 1px solid ${colors.NEUTRAL_100};
              border-radius: 8px;
              color: ${colors.NEUTRAL_50};
              font-family: 'Inter', sans-serif;
              font-size: 14px;
              font-weight: 500;
              cursor: pointer;
              transition: all 0.2s ease;
              &:hover {
                background: rgba(255, 255, 255, 0.05);
                border-color: ${colors.NEUTRAL_50};
              }
              &:disabled {
                opacity: 0.6;
                cursor: not-allowed;
              }
            `}
          >
            Reset All
          </button>
        )}

        {(capabilities?.can_approve || capabilities?.can_apply) && (
          <button
            type='button'
            onClick={onApproveCompleted}
            disabled={retrying}
            css={css`
              padding: 10px 24px;
              background: ${colors.SUCCESS_300};
              border: none;
              border-radius: 8px;
              color: ${colors.WHITE};
              font-family: 'Inter', sans-serif;
              font-size: 14px;
              font-weight: 600;
              cursor: pointer;
              transition: background 0.2s ease;
              &:hover {
                background: ${colors.SUCCESS_400};
              }
              &:disabled {
                opacity: 0.6;
                cursor: not-allowed;
              }
            `}
          >
            Review Completed Divisions
          </button>
        )}
      </div>

      {/* Note about partial apply */}
      {(capabilities?.can_approve || capabilities?.can_apply) && (
        <p
          css={css`
            font-family: 'Inter', sans-serif;
            font-size: 12px;
            color: ${colors.NEUTRAL_70};
            margin: 12px 0 0;
            text-align: center;
          `}
        >
          Only completed divisions will be included when applied.
        </p>
      )}
    </div>
  )
}

export default PartiallyGeneratedState
